import React, { PropsWithChildren } from "react";
import styled from "styled-components";

export type RowProps = {};

export type CellProps = {
  textAlign?: "left" | "right" | "center";
  width?: string;
  noWrap?: boolean;
};
const cellTextAlignment = ({ textAlign = "center" }: CellProps) => textAlign;
const cellWidth = ({ width = "auto" }: CellProps) => width;
const cellNowrap = ({ noWrap = false }: CellProps) => (noWrap && "white-space: nowrap") || "";

const Thead = styled.thead``;

const Tbody = styled.tbody`
  &:nth-child(odd) {
    background-color: rgba(54,56,61,.05);
  }
`;

const Row = styled.tr<RowProps>``;

export const Table = {
  Main: styled.table`
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
  `,

  Header: {
    Row({ children, ...props }: PropsWithChildren<RowProps>) {
      return (
        <Thead>
          <Row {...props}>{children}</Row>
        </Thead>
      );
    },

    Cell: styled.th<CellProps>`
      font-weight: 400;
      vertical-align: baseline;
      text-align: ${cellTextAlignment};
      width: ${cellWidth};
      ${cellNowrap};
      padding: 10px;
      border-bottom: 1px solid rgba(54,56,61,.333);
      color: #36383d;
    `,
  },

  Row({ children, ...props }: PropsWithChildren<RowProps>) {
    return (
      <Tbody>
        <Row {...props}>{children}</Row>
      </Tbody>
    );
  },

  Label: styled.th``,

  Cell: styled.td<CellProps>`
    font-weight: 300;
    text-align: ${cellTextAlignment};
    ${cellNowrap};
    color: #36383d;
  `,
};

export const Grid = {

  ColumnTable: styled.div.attrs({ className: "gs-table" }) <{ hyperLinkTextDecorationStyle?: "solid" | "dotted" }> `
    white-space: nowrap;
    font-family: Rubik, Arial, sans-serif;
    font-size: 14px;
    font-stretch: 100%;
    line-height: 14px;
    font-weight: 300;
    color: #36383d;
    width: 100%;
    display: flex;
    position: relative;

    a {
      text-decoration: none;
      border-bottom: ${(p) => p.hyperLinkTextDecorationStyle === "solid" ? "1px solid rgba(54,56,61,.333);" : "1px dotted rgba(54,56,61,.333);"};
      color: inherit;
    }

    .hover{
      background-color:rgba(54,56,61,.2) !important;
    }

    * {
      border-collapse: collapse;
      border-spacing: 0;
      box-sizing: border-box;
    }
  `,

  Fixed: styled.div.attrs({ className: "fixed-cols" })`
    flex-grow: 1;
    flex-basis: 0px;
    display: flex;
    font-family: Rubik, Arial, sans-serif;
  `,

  Flexible: styled.div.attrs({ className: "flexible-cols" }) <{ flexibleColumnsSectionMaxWidth?: number }>`
    flex-grow: 2;
    flex-basis: 0px;
    display: flex;
    overflow-x: auto;
    position: relative;
    ${(p) => p.flexibleColumnsSectionMaxWidth && `max-width: ${p.flexibleColumnsSectionMaxWidth}px`}
  `,

  Column: styled.div.attrs(props => ({ className: (props.className || '') + " column" })) <{
    sorted?: boolean;
    notSortable?: boolean;
    tableIsSmall?: boolean;
  }>`
    flex-grow: 1;
    flex-basis: 0px;
    display: flex;
    flex-direction: column;
    position: relative;
    ${(p) => !p.notSortable && p.sorted && "background-color: #EEEEEEA0;"}
  `,

  Row: styled.div.attrs({ className: "row" })``,

  ColumnHeader: styled.div.attrs({ className: "column-header" }) <{ alignLeft?: boolean; notSortable?: boolean; tableIsSmall?: boolean; removeColumnHeaderBorder?: boolean; smallTableHeaders?: boolean; boldTableHeaders?: boolean; underlineTableHeaders?: boolean; sorted?: boolean; }>`
    transition: opacity .3s ease-in;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background-color: ${(p) => !p.notSortable && p.sorted && "#EEEEEE" || "#FFFFFF"};
    min-height: 36px;
    min-width: ${(p) => (p.tableIsSmall ? "25px" : "50px")};
    padding: ${(p) => (p.alignLeft ? "10px 10px 10px 5px" : "10px")};
    text-align: center;
    font-size: ${(p) => p.smallTableHeaders ? ".7rem" : "inherit"};
    display: flex;
    justify-content: ${(p) => (p.alignLeft ? "flex-start" : "center")};
    align-items: "center";
    border-bottom:${(p) => p.removeColumnHeaderBorder ? "" : "1px solid rgba(54,56,61,.333)"}; 
    font-weight: ${(p) => p.boldTableHeaders ? 500 : 400};
    font-family: Rubik, Arial, sans-serif;
    cursor: ${(p) => (!p.notSortable ? "pointer" : "default")};
    user-select: none;
    color: #36383d;
    text-decoration: ${(p) => p.underlineTableHeaders && !p.notSortable ? "underline" : "none"} ;
    .subdata{
      color: rgba(54,56,61,.5);
      font-size:12px;
    }
  `,

  RowHeader: styled.div.attrs(props => ({ className: "row-header " + (props.className || "") })) <{ alignLeft?: boolean; notSortable?: boolean; tableIsSmall?: boolean; }>`
    padding: ${(p) => (p.alignLeft ? "5px 5px 5px 5px" : "5px")};
    flex-direction: column;
    display: flex;
    min-width: 48px;
    min-height: 36px;
    justify-content: ${(p) => (p.alignLeft ? "center" : " center")};
    align-items: center;
    text-align: ${(p) => (p.alignLeft ? "left" : "center")};
    white-space: break-spaces;
    line-height: 1.25;
    color: #36383d;

    .subdata{
      color: rgba(54,56,61,.5);
      width: ${(p) => (p.alignLeft && "100%")};
      font-size:12px;

      display:flex;
      text-align: ${(p) => (p.alignLeft ? "left" : "center")};
    }

    &:nth-child(odd) {
      background-color: rgba(54,56,61,.05);
    }

    &:last-child {
      width: 100%;
    }

    ::-webkit-scrollbar{
      display:none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
  `,

  Cell: styled.div.attrs(props => ({ className: "cell " + (props.className || "") })) <{ tableIsSmall?: boolean; alignLeft?: boolean; }>`    
    text-align: ${(p) => (p.alignLeft ? "left" : "center")};
    min-width: 48px;
    min-height: 36px;
    padding-left: ${(p) => (p.alignLeft ? "5px" : " 0")};

    display: flex;
    justify-content: ${(p) => (p.alignLeft ? "flex-start" : "center")};
    flex-direction: column;
    align-items: "center";

    &:nth-child(odd) {
      background-color: rgba(54,56,61,.05)
    }
    .subdata{
      color: rgba(54,56,61,.5);
      font-size:12px;
    }

    ::-webkit-scrollbar{
      display:none;
    }

    -ms-overflow-style: none;
    scrollbar-width: none;
  `,

  Controls: styled.div.attrs({ className: "gs-table-controls" })`
    margin-top: 6px;
    display: flex;
    justify-content: space-between;

    // styling for the export button and icon
    .export-button {
      display: flex;
      align-items: center;
      flex-direction: row;

      .export-icon-container {
        position: relative;
        height: 18px;
        width: 18px;

        svg{
          position: relative;
          height: 32px;
          width: 32px;
          top: -4px;
          left: -1px;
        }
      }
    }

    .tip-container {
      .safe-position {
        position: relative;
        background-color: #36383d;
        text-align: center;
        padding: 4px 8px;

        span {
          color: white;
          font-size: 14px;
          font-family: Rubik, Arial, sans-serif;
          font-weight: 300;
        }

        &:after {
          position: absolute;
          content: "";
          width: 0px;
          height: 0px;
        }

        &[class*='arrow-top-']:after {
          border-bottom: 6px solid #36383d;
          border-right: 6px solid transparent;
          border-left: 6px solid transparent;
          top: -6px;
        }

        &[class*='arrow-right-']:after {
          border-left: 6px solid #36383d;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          right: -6px;
          top: calc(50% - 6px);
        }

        &[class*='arrow-bottom-']:after {
          border-top: 6px solid #36383d;
          border-right: 6px solid transparent;
          border-left: 6px solid transparent;
          bottom: -6px;
        }

        &[class*='arrow-left-']:after {
          border-right: 6px solid #36383d;
          border-top: 6px solid transparent;
          border-bottom: 6px solid transparent;
          left: -6px;
          top: calc(50% - 6px);
        }

        &.arrow-bottom-center:after, &.arrow-top-center:after {
          left: calc(50% - 6px);
        }

        &.arrow-bottom-left-edge:after, &.arrow-top-left-edge:after {
          left: 1px;
        }

        &.arrow-bottom-right-edge:after, &.arrow-top-right-edge:after {
          right: 1px;
        }
      }
    }
  `,

  Pagination: styled.div``,
};
